import { useEffect, useState } from "react";
import { Download, FileText, Loader2, X } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { fileKindLabel, filePreviewKind } from "@/lib/activityFiles";
import { pdfViewerUrl } from "@/lib/slideDecks";
import TextbookReader from "@/components/student/TextbookReader";

type PreviewData = {
  kind: "html" | "pdf" | "image" | "download";
  html?: string | null;
  url?: string;
  name?: string;
  mime?: string;
};

function downloadHref(url: string, name: string) {
  return (
    <a className="student-file-viewer__btn" href={url} download={name} target="_blank" rel="noreferrer">
      <Download size={16} aria-hidden />
      Download
    </a>
  );
}

export default function StudentFileLink({
  part,
  activityId,
  previewPath,
}: {
  part: { url: string; name: string; mime?: string; mediaId?: string; s3Key?: string };
  activityId?: string;
  /** Word preview for a student hand-in (query `s3Key`). */
  previewPath?: string;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState<PreviewData | null>(null);
  /** The PDF couldn’t be drawn as a book (e.g. the file host blocks it): use the plain viewer. */
  const [plainPdf, setPlainPdf] = useState(false);
  const kind = filePreviewKind(part.mime, part.name);
  const label = fileKindLabel(part.mime, part.name);

  const bookUrl = !loading && preview?.kind === "pdf" && preview.url && !plainPdf ? preview.url : "";

  useEffect(() => {
    if (!open || bookUrl) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, bookUrl]);

  const load = async () => {
    setOpen(true);
    setError("");
    const localKind = filePreviewKind(part.mime, part.name);
    if (localKind === "pdf" || localKind === "image") {
      setPreview({ kind: localKind, url: part.url, name: part.name, mime: part.mime });
      setLoading(false);
      return;
    }
    if (localKind === "other") {
      setPreview({ kind: "download", url: part.url, name: part.name, mime: part.mime });
      setLoading(false);
      return;
    }
    setLoading(true);
    setPreview(null);
    try {
      const path =
        previewPath && part.s3Key
          ? `${previewPath}${previewPath.includes("?") ? "&" : "?"}s3Key=${encodeURIComponent(part.s3Key)}`
          : activityId
        ? `/api/student/activities/${encodeURIComponent(activityId)}/files/preview?${
            part.mediaId
              ? `mediaId=${encodeURIComponent(part.mediaId)}`
              : `url=${encodeURIComponent(part.url)}`
          }`
        : part.mediaId
          ? `/api/media/${encodeURIComponent(part.mediaId)}/preview`
          : "";
      if (!path) {
        setPreview({ kind: "download", url: part.url, name: part.name, mime: part.mime });
        return;
      }
      const env = await api<{ success: boolean; data: PreviewData }>(path);
      setPreview(env.data);
    } catch (err) {
      setError(errorMessage(err));
      setPreview({ kind: "download", url: part.url, name: part.name, mime: part.mime });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button type="button" className="student-file" onClick={() => void load()}>
        <FileText size={16} aria-hidden />
        <span className="student-file__copy">
          <strong>{part.name}</strong>
          <span className="student-file__meta">{label}</span>
        </span>
      </button>
      {open && bookUrl ? (
        <TextbookReader url={bookUrl} name={part.name} onClose={() => setOpen(false)} onFallback={() => setPlainPdf(true)} />
      ) : null}
      {open && !bookUrl ? (
        <div className="student-file-viewer" role="dialog" aria-modal="true" aria-label={part.name}>
          <div className="student-file-viewer__bar">
            <span className="student-file-viewer__name">{part.name}</span>
            <span className="student-file-viewer__actions">
              {downloadHref(part.url, part.name)}
              <button
                type="button"
                className="student-file-viewer__btn"
                onClick={() => setOpen(false)}
              >
                <X size={16} aria-hidden />
                Close
              </button>
            </span>
          </div>
          <div className="student-file-viewer__stage">
            {loading ? (
              <p className="student-file-viewer__status">
                <Loader2 size={18} aria-hidden /> Loading preview…
              </p>
            ) : null}
            {!loading && preview?.kind === "html" && preview.html ? (
              <div className="student-file-doc" dangerouslySetInnerHTML={{ __html: preview.html }} />
            ) : null}
            {!loading && preview?.kind === "pdf" && preview.url ? (
              <iframe className="student-file-frame" title={part.name} src={pdfViewerUrl(preview.url)} />
            ) : null}
            {!loading && preview?.kind === "image" && preview.url ? (
              <img className="student-file-image" src={preview.url} alt={part.name} />
            ) : null}
            {!loading && preview?.kind === "download" ? (
              <p className="student-file-viewer__status">
                {error || "This file can’t be previewed here. Download it to open it."}
              </p>
            ) : null}
            {!loading && kind === "word" && preview?.kind === "html" && !preview.html ? (
              <p className="student-file-viewer__status">This file can’t be previewed here. Download it to open it.</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
